import { isNative, isIOS, isAndroid } from './platform';
import { initDeepLinks } from './deepLinks';

/**
 * Inicialización específica de la app nativa (Capacitor): status bar y splash.
 *
 * - Es **no-op en web**: si no corremos en nativo, retorna sin cargar nada de
 *   Capacitor (los imports son dinámicos, así no entran en el bundle web).
 * - Se llama una sola vez al arrancar (desde `main.tsx`).
 */
export async function initNativeApp(): Promise<void> {
  if (!isNative()) return;

  // Clase de plataforma para ajustes CSS específicos (p. ej. el floor de
  // --safe-area-top en Android definido en _globals.scss).
  const root = document.documentElement;
  if (isAndroid()) root.classList.add('platform-android');
  if (isIOS()) root.classList.add('platform-ios');

  // OTA (Capgo self-hosted): confirmar cuanto antes que este bundle arranca
  // bien — sin notifyAppReady() el plugin hace ROLLBACK automático al bundle
  // anterior pasado su timeout. Aislado del resto: un fallo aquí no debe
  // impedir la inicialización de la app (ni viceversa).
  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    void CapacitorUpdater.notifyAppReady();
  } catch (err) {
    console.error('CapacitorUpdater notifyAppReady failed:', err);
  }

  try {
    const [{ StatusBar, Style }, { SplashScreen }] = await Promise.all([
      import('@capacitor/status-bar'),
      import('@capacitor/splash-screen'),
    ]);

    // Barra de estado del sistema VISIBLE (sin modo pantalla completa).
    // iOS: overlay:false — el SO coloca el WebView DEBAJO de la Dynamic Island /
    //   notch (adaptativo por dispositivo). No dependemos de env(safe-area-inset-top),
    //   que en este WKWebView devuelve 0 aunque el WebView vaya edge-to-edge.
    //   El header aporta su padding base sobre el borde ya despejado.
    if (isIOS()) {
      await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
      // Franja de la barra = beige de marca (el plugin la deja negra por defecto).
      await StatusBar.setBackgroundColor({ color: '#f5f1e8' }).catch(() => {});
      // Texto de la barra oscuro (Style.Light = darkContent) para leerse sobre el beige.
      await StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    }

    // Android 15 FUERZA edge-to-edge: el WebView se dibuja DEBAJO de las barras
    // del sistema (estado y navegación). Los insets reales los inyecta el plugin
    // core SystemBars de Capacitor como --safe-area-inset-* (ver _globals.scss,
    // que los encadena en --safe-area-*). NO inyectar aquí --safe-area-top inline:
    // ganaría a la cadena de _globals y duplicaría el hueco en los WebView
    // antiguos donde SystemBars compensa con padding nativo (e inyecta 0).
    if (isAndroid()) {
      // Iconos de la barra en oscuro (Style.Light = fondo claro → iconos oscuros).
      await StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    }

    // Deep links (Universal Links / App Links): enrutan al abrir por un enlace.
    void initDeepLinks();

    // Ocultar el splash una vez la app ya ha montado.
    await SplashScreen.hide();
  } catch (err) {
    // Nunca debe tumbar el arranque de la app por un fallo de plugin nativo.
    console.error('initNativeApp failed:', err);
  }
}
